import { provideZoneChangeDetection } from "@angular/core";
import { bootstrapApplication } from '@angular/platform-browser';
import * as Sentry from '@sentry/angular';
import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';
import { environment } from './environments/environment';

// Sentry : remontée des erreurs du navigateur (offre gratuite, région UE).
// Données de santé => aucune donnée personnelle envoyée (voir nettoyerEvenementSentry).
// DSN vide (valeur par défaut) = Sentry n'est pas initialisé : rien ne change.
if (environment.sentryDsn) {
  Sentry.init({
    dsn: environment.sentryDsn,
    environment: environment.sentryEnvironment,
    // Ni IP, ni cookies, ni utilisateur.
    sendDefaultPii: false,
    // Pas de suivi de performance (pas de tracesSampleRate) ni de Session Replay :
    // aucune intégration correspondante n'est ajoutée. Les logs console ne sont pas
    // gardés comme contexte (ils peuvent afficher des données métier).
    integrations: [Sentry.breadcrumbsIntegration({ console: false })],
    beforeSend: nettoyerEvenementSentry,
  });
}

bootstrapApplication(AppComponent, {...appConfig, providers: [provideZoneChangeDetection(), ...appConfig.providers]})
  .catch(err => console.error(err));

/**
 * Dernier nettoyage avant envoi à Sentry : retire la requête (URL, en-têtes, cookies),
 * l'utilisateur, les paramètres des URL citées dans les messages d'erreur, et le contexte
 * (breadcrumbs) dont l'URL contient des paramètres (?token=..., #...).
 */
function nettoyerEvenementSentry(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  delete event.request;
  delete event.user;

  if (event.message) {
    event.message = urlsSansParametres(event.message);
  }
  for (const exception of event.exception?.values ?? []) {
    if (exception.value) {
      exception.value = urlsSansParametres(exception.value);
    }
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.filter(b => !contientUrlAvecParametres(b));
  }
  return event;
}

/** Vrai si une des URL d'un breadcrumb (navigation, appel HTTP) contient des paramètres. */
function contientUrlAvecParametres(breadcrumb: Sentry.Breadcrumb): boolean {
  const data = breadcrumb.data ?? {};
  return [data['url'], data['from'], data['to'], breadcrumb.message].some(
    v => typeof v === 'string' && /[?#]/.test(v)
  );
}

/** Coupe les paramètres (?... et #...) de toutes les URL présentes dans un texte. */
function urlsSansParametres(texte: string): string {
  return texte.replace(/(https?:\/\/[^\s?#]*)[?#][^\s]*/g, '$1');
}
